<template>
  <div>
    <h4 class="mb-1 text-center">
      {{ $t("action_plan.tasks") }}
      <span v-if="preview"> ({{ $t("action_plan.viewer.preview") }}) </span>
    </h4>
    <p
      v-if="config.alarm"
      class="text-center"
      @click="setAlarm(config.alarm)"
    >
      {{ $t("grid.slots.info.alarm_id") }}:
      <span class="text-id-alarm d-inline-flex align-items-center fw-bold clickable" style="cursor: pointer;">
        {{ config.alarm.id }}
      </span>
    </p>
    <hr class="my-1" />
    <div v-if="ready">
      <navigation-component
        v-model="history"
        :action-plan="actionPlan"
        :parent-container="parentContainer"
        :preview="preview"
        :validating="validating"
        :config="config"
        @update-action-plan-log="$emit('updated')"
      />
      <div
        v-if="showValidateActionPlanButton && $can('validate_action_plans', 'ActionPlan')"
        class="d-flex justify-content-end"
      >
        <b-button variant="primary" @click="confirmValidate()">
          {{ $t("action_plan.validate") }}
        </b-button>
      </div>
    </div>
    <div class="pb-1 text-center" v-else>
      <b-spinner label="Spinning" variant="primary"></b-spinner>
    </div>
  </div>
</template>

<script>
import axios from "@axios";
import NavigationComponent from "./NavigationComponent.vue";
import store from '@/store';
import { mergeOperationalFormAnswerResultsIntoLog } from "@/libs/operationalFormAnswerResultsHydration";

export default {
  components: { NavigationComponent },
  props: {
    actionPlanId: {
      type: String | Number,
      required: true,
    },
    value: Object,
    config: {
      type: Object,
      default: () => {
        return {
          uuid: null,
          name: null,
        };
      },
    },
    preview: {
      type: Boolean,
      default: false,
    },
    validating: {
      type: Boolean,
      default: false,
    },
    parentContainer: {
      type: HTMLDivElement,
      default: null,
    },
    currentLocation: {
      type: String,
    },
    data: {
      type: Object,
      require: false,
    },
  },
  data() {
    return {
      ready: false,
      actionPlan: [],
      history: [],
      showValidateButton: false,
      dateValidation: null,
      userValidation: null,
    };
  },
  watch: {
    value: {
      handler(next) {
        const normalized =
          next && typeof next === "object" ? next : {};
        mergeOperationalFormAnswerResultsIntoLog(
          next && typeof next === "object" ? next : null
        );
        this.history = normalized;
      },
      immediate: true,
    },
    history(val) {
      this.$emit("input", val);
    },
  },
  async mounted() {
    await this.getJsonLogs();

    if (this.data && this.data.hasOwnProperty("uuid_action_plan_log")) {
      await this.checkActionPlanLog();
    }
  },
  computed: {
    showValidateActionPlanButton() {
      if (this.currentLocation !== "action_plan_logs") return false;

      return this.showValidateButton && !this.dateValidation;
    },
  },
  methods: {
    setAlarm(alarm) {
       store.dispatch('grid/addAlarmInfoToAvailableSlot', {
        ...alarm,
        analytic_record: (alarm.analytic_record.length) ? alarm.analytic_record : null,
        facial_recognition: (alarm.alarm_type.acronym === 'FR') ? {'alarm_id': alarm.id} : {},
        tenant: alarm.router.tenant,
        unit: alarm.router.unit,
        unit_coordinates: {
          lat: alarm.router.lat,
          lng: alarm.router.lng
        }
      });
    },
    async getJsonLogs() {
      await axios
        .get(`/v1/action_plan/get-action-plan-json/${this.actionPlanId}`)
        .then(({ data }) => {
          this.ready = true;
          this.actionPlan = data.data;
          this.checkLog();
          this.$nextTick(() => this.$emit("rendered"));
        })
        .catch((err) => {});
    },
    async checkActionPlanLog() {
      await axios
        .get(`/v1/action_plan/logs/${this.config.uuid}`)
        .then(({ data }) => {
          this.dateValidation = data.data.validated_at;
          this.userValidation = data.data.user_validation
            ? data.data.user_validation.name
            : null;
        })
        .catch((err) => {});

      this.checkLog();
    },
    onAnswerSelected(answere) {
      this.addToHistory(this.currentNode);
      this.currentNode = this.currentNode.answers[answere];
    },
    onPrevClicked() {
      if (this.history.length) {
        const node = this.history.pop();
        this.currentNode = node === "root" ? this.root : node;
      }
    },
    addToHistory(node) {
      if (node.root) {
        this.history.push("root");
        return;
      }

      if (!this.history.find((n) => node.id == n.id)) {
        this.history.push(node);
      }
    },
    confirmValidate() {
      const title = `${this.$t("action_plan.validate")} ${this.$t("action_plan.title")} ${
        this.actionPlan.name
      }`;

      this.$swal({
        title: title,
        text: "",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `${this.$t("action_plan.validate")}`,
        cancelButtonText: `${this.$t("actions.cancel")}`,
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("action_plan/actionPlanValidate", this.config.uuid)
            .then((response) => {
              const { message } = response;

              this.$swal({
                title: `${this.$t("action_plan.title")} ${this.$t(
                  "action_plan.validated"
                )}`,
                text: "",
                icon: "success",
              });

              this.$emit("validate-success");
              this.checkActionPlanLog();
            })
            .catch(() => {
              this.$swal({
                title: "Error",
                text: "",
                icon: "error",
              });
            });
        }
      });
    },
    checkLog() {
      if (this.history && this.history?.history) {
        const elementTerminateType = this.history.history.find(
          (element) => element.type === "Terminate"
        );

        if (elementTerminateType) {
          this.showValidateButton = true;
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.dark-layout .action-plan-log-container hr {
  border-color: var(--main-light) !important;
}

.dark-layout .border-bottom {
  border-color: var(--main-light) !important;
}
</style>
